import { Button, Input, Menu, Popover, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { ReactElement } from 'react';
import { ReactComponent as ReplyIcon } from '../../../assets/images/svg/forward-reply.svg';
import CustomFilterDropdownIcon from '../../../shared/components/filterDropdownComponent/CustomFilterDropdownIcon';
import NumberFormatView from '../../../shared/components/NumberFormatView/NumberFormatView';
import { CRM_INPATIENT_STATUS_COLOUR, getColumnSearchDropdown, IP_DROPDOWN_SEARCH_INPUT_TYPES } from '../../../shared/constants/crmConstants';
import { getDateWithTextMonth, getDateWithTime } from '../../../utils/dateTimeUtils';
import { canActivate, getTruncatedString } from '../../../utils/utility';
import { getDefaultStr } from '../../crm/inPatient/service/crmUtils';
import { ReactComponent as Information } from '../../../assets/images/svg/i_information.svg';
import ApplicationDetail from '../../../shared/services/applicationDetail.service';
import { Payer } from '../../../shared/models/payer.model';
import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import { authFeatureConstants } from '../../../shared/constants/commonConstants';

export const STATUS_COLOR_MAP = {
  2: {
    color: '#E1555421',
    textColor: '#E15554',
    text: 'Open',
  },
  3: {
    color: '#E1BC2921',
    textColor: '#E1BC29',
    text: 'Pending',
  },
  4: {
    color: '#3BB27321',
    textColor: '#3BB273',
    text: 'Resolved',
  },
  5: {
    color: 'rgba(57, 35, 119, 0.13)',
    textColor: '#201B7C',
    text: 'Closed',
  },
};

export const getIsPageNameReplyGrievance = (pathname: string): boolean => {
  return pathname.includes('/reply') && pathname.includes('grievance/view/detail/');
};
const menu = (onClose: any, record: any, value: any): any => (
  <Menu>
    {!(STATUS_COLOR_MAP[value]?.text === 'Closed') ?
      <Button
        type="link" className="new-search-dropdown"
        onClick={(ev: any): any => { ev.stopPropagation(); onClose(record); }}>Close</Button> :
      <></>
    }
  </Menu>
);

export const View_Grievance = {
  TICKET_ID: {
    title: 'Ticket ID',
    dataIndex: 'ticketId',
    key: 'ticketId',
    sorter: true,
    filterIcon: (): any => <CustomFilterDropdownIcon filterKey="ticketId" />,
    ...getColumnSearchDropdown({
      filterKey: 'ticketId',
      searchFilterKey: 'ticketId',
      inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
      placeholder: 'Search Ticket ID',
    }),
  },
  PATIENT_NAME: {
    title: 'Patient Name',
    dataIndex: 'patientName',
    key: 'patientName',
    sorter: true,
    filterIcon: (): any => <CustomFilterDropdownIcon filterKey="patientName" />,
    ...getColumnSearchDropdown({
      filterKey: 'patientName',
      searchFilterKey: 'patientName', // Ensure consistent casing
      inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
      placeholder: 'Search Patient Name',
    }),
  },
  PAYER_CLAIM_NO: {
    title: 'Payer Claim No',
    dataIndex: 'payerClaimNo',
    key: 'payerClaimNo',
    filterIcon: (): any => <CustomFilterDropdownIcon filterKey="payerClaimNo" />,
    ...getColumnSearchDropdown({
      filterKey: 'payerClaimNo',
      searchFilterKey: 'payerClaimNo',
      inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
      placeholder: 'Search Payer Claim Number',
    }),
  },
  PAYER_NAME: {
    title: 'Payer Name',
    dataIndex: 'payerName',
    key: 'payerName',
    sorter: true,
    filterIcon: (): any => <CustomFilterDropdownIcon filterKey="payerName" />,
    ...getColumnSearchDropdown({
      filterKey: 'payerName',
      searchFilterKey: 'payerName',
      inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
      placeholder: 'Search Payer Name',
    }),
  },

  TICKET_STATUS: {
    title: 'Ticket Status',
    dataIndex: 'ticketStatus',
    key: 'ticketStatus',
    sorter: false,
    render: (status: string, data: any): any => {
      return (
        <span>{status}</span>
      );
    },
    filterIcon: (): any => <CustomFilterDropdownIcon filterKey="ticketStatus" />,
    ...getColumnSearchDropdown({
      filterKey: 'ticketStatus',
      searchFilterKey: 'ticketStatus',
      inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.MULTISELECT_DROPDOWN,
      placeholder: 'Search Ticket Status',
      optionPath: '[\'lookUp\'][\'grievanceStatus\']',
    }),
  },
  GRIEVANCE_REASON: {
    title: 'Grievance Reason',
    dataIndex: 'reason',
    key: 'reason',
    filterIcon: (): any => <CustomFilterDropdownIcon filterKey="reason" />,
    ...getColumnSearchDropdown({
      filterKey: 'reason',
      searchFilterKey: 'reason',
      inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
      placeholder: 'Search',
    }),
  },
  CREATED_ON: {
    title: 'Created On',
    dataIndex: 'createdOn',
    key: 'createdOn',
    sorter: true,
    render: (createdOn: any): any => createdOn ? getDateWithTime(createdOn) : '-',
    filterIcon: (): any => <CustomFilterDropdownIcon filterKey="createdOn" />,
    ...getColumnSearchDropdown({
      filterKey: 'createdOn',
      searchFilterKey: 'createdOn',
      inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.DATE_RANGE,
      placeholder: 'Search',
    }),
  },
  LAST_RESPONSE: {
    title: 'Last Response',
    dataIndex: 'modifiedOn',
    key: 'modifiedOn',
    sorter: true,
    render: (modifiedOn: any): any => modifiedOn ? getDateWithTime(modifiedOn) : '-',
    filterIcon: (): any => <CustomFilterDropdownIcon filterKey="modifiedOn" />,
    ...getColumnSearchDropdown({
      filterKey: 'modifiedOn',
      searchFilterKey: 'modifiedOn',
      inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.DATE_RANGE,
      placeholder: 'Search',
    }),
  },
};

export const getGrievanceColumns = ({ reply, onClose }: any): ColumnsType<any[]> => [
  {
    title: 'RequestID',
    dataIndex: 'id',
    key: 'id',
    width: 200,
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
    }: any): any => {
      return (
        <div className="width-search">
          <Input
            className="search-margin"
            autoFocus={true}
            placeholder="Type text here"
            value={selectedKeys[0]}
            onChange={(e: any): any => {
              setSelectedKeys(e?.target?.value ? [e?.target?.value] : []);
            }}
          />
          <Button
            className="margin-search"
            onClick={(): any => {
              confirm();
            }}
            type="primary"
          >
            Search
          </Button>
        </div>
      );
    },
    filterIcon: (): any => {
      return <SearchOutlined />;
    },
    onFilter: (value: any, record: any): any => {
      const id = record?.id?.toString().toLowerCase();
      return id.toLowerCase().includes(value?.toLowerCase());
    },
    render: (value: any): ReactElement => (
      <div className="text-center">{value}</div>
    ),
  },
  {
    title: 'Subject',
    dataIndex: 'subject',
    key: 'subject',
    width: 300,
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
    }: any): any => {
      return (
        <div className="width-search">
          <Input
            className="search-margin"
            autoFocus={true}
            placeholder="Type text here"
            value={selectedKeys[0]}
            onChange={(e: any): any => {
              setSelectedKeys(e?.target?.value ? [e?.target?.value] : []);
            }}
          />
          <Button
            className="margin-search"
            onClick={(): any => {
              confirm();
            }}
            type="primary"
          >
            Search
          </Button>
        </div>
      );
    },
    filterIcon: (): any => {
      return <SearchOutlined />;
    },
    onFilter: (value: any, record: any): any => {
      const subject = record?.subject?.toString().toLowerCase();
      return subject.toLowerCase().includes(value?.toLowerCase());
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 150,
    render: (value: any, record: any): ReactElement => (
      <Tag color={STATUS_COLOR_MAP[value]?.color || 'success'}>
        <strong style={{ color: STATUS_COLOR_MAP[value]?.textColor || '#00000' }}>
          {STATUS_COLOR_MAP[value]?.text || value}
        </strong>
        {!(STATUS_COLOR_MAP[value]?.text === 'Closed') ?
          <Popover content={menu(onClose, record, value)}>
            <strong style={{ color: STATUS_COLOR_MAP[value]?.textColor }} onClick={(e: any): any => e.preventDefault()}>
              <DownOutlined className="popover-margin" />
            </strong>
          </Popover> : <></>
        }
      </Tag>
    ),
  },
  {
    title: 'Last response',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 150,
    render: (value: any): ReactElement => (
      <div>{getDateWithTextMonth(value)}</div>
    ),
  },
  ...(
    canActivate(authFeatureConstants.EDIT_GRIEVANCE) ? [
      {
        title: 'Reply',
        dataIndex: 'requestId',
        key: 'requestId',
        width: 150,
        render: (text: any, record: any): any => {
          return (
            <Button size="small" type="text" onClick={(ev: any): void => { ev.stopPropagation(); reply(record); }}>
              <ReplyIcon />
            </Button>
          );
        },
      },
    ] : []
  ),
];
export const getGrievanceColumnsToolTip = (): ColumnsType<any[]> => [
  {
    title: 'Request ID',
    dataIndex: 'id',
    key: 'id',
    width: 10,
  },
  {
    title: 'Subject',
    dataIndex: 'subject',
    key: 'subject',
    width: 50,
  },
];
export const getGrievanceTableColumns = (): ColumnsType<any[]> => [
  {
    title: 'S.NO',
    dataIndex: 'sno',
    key: 'sno',
    width: 10,
  },
  {
    title: 'Claim Number',
    dataIndex: 'claimNumber',
    key: 'claimNumber',
    width: 50,
  },
  {
    title: 'Patient Name',
    dataIndex: 'patientName',
    key: 'patientName',
    width: 50,
  },
  {
    title: 'Claimed Amount',
    dataIndex: 'claimedAmount',
    key: 'claimedAmount',
    width: 50,
  },
  {
    title: 'Approved Amount',
    dataIndex: 'approvedAmount',
    key: 'approvedAmount',
    width: 50,
  },
  {
    title: 'Actions',
    dataIndex: 'actions',
    key: 'actions',
    width: 50,
  },
  {
    title: 'Comments',
    dataIndex: 'comments',
    key: 'comments',
    width: 50,
  },
];
export const getGrievanceTableMapping = (isNewAdmissionBOH: boolean): any => {
  const grievanceTableMappingColumns = {
    PAYER_NAME: {
      title: <Tooltip title="TPA" placement="bottom">Payer</Tooltip>,
      dataIndex: 'payerName',
      key: 'payerName',
      className: 'text-elipsis',
      render: (payerName: any, row: any): any => (
        getTruncatedString(getDefaultStr(payerName, true))
      ),
      sorter: true,
      sortOrder: false,
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="payerId" />,
      ...getColumnSearchDropdown({
        filterKey: 'payerId',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.MULTISELECT_DROPDOWN,
        placeholder: 'Search payer',
        optionPath: '[\'payerList\'][\'allPayers\']',
      }),
    },
    INSURER_NAME: {
      title: 'Insurer Name',
      dataIndex: 'insurerName',
      key: 'insurerName',
      className: 'text-elipsis',
      render: (insurerName: any, row: any): any => (
        <Tooltip placement="right" title={row.insurerName}>
          <div className="text-elipsis insurer-name-column">
            {row.insurerName}
          </div>
        </Tooltip>
      ),
      sorter: true,
      sortOrder: false,
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="insurerName" />,
      ...getColumnSearchDropdown({
        filterKey: 'insurerName',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.MULTISELECT_DROPDOWN,
        placeholder: 'Select insurer names',
        optionPath: '[\'lookUp\'][\'allInsurers\']',
      }),
    },
    IP_NO: {
      title: 'IP No.',
      dataIndex: 'inPatientNo',
      key: 'inPatientNo',
      render: (inPatientNo: any): any => (
        getDefaultStr(inPatientNo, true)
      ),
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="inPatientNo" />,
      ...getColumnSearchDropdown({
        filterKey: 'inPatientNo',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
        placeholder: 'Search IP No.',
      }),
    },
    MAID: {
      title: 'Member Id',
      dataIndex: 'memberId',
      key: 'memberId',
      render: (memberId: any): any => (
        getDefaultStr(memberId, true)
      ),
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="memberId" />,
      ...getColumnSearchDropdown({
        filterKey: 'memberId',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
        placeholder: 'Search Member Id',
      }),
    },
    policy_number: {
      title: 'Policy Number',
      dataIndex: 'policyNo',
      key: 'policyNo',
      render: (policyNo: any): any => (
        getDefaultStr(policyNo, true)
      ),
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="policyNo" />,
      ...getColumnSearchDropdown({
        filterKey: 'policyNo',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
        placeholder: 'Search Policy Number',
      }),
    },
    CLAIM_NO: {
      title: 'Claim No.',
      dataIndex: 'payerClaimNo',
      key: 'payerClaimNo',
      className: 'text-elipsis',
      render: (claimNo: any): any => (
        getDefaultStr(claimNo, true)
      ),
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="payerClaimNo" />,
      ...getColumnSearchDropdown({
        filterKey: 'payerClaimNo',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
        placeholder: 'Search Claim No.',
      }),
    },
    MEMBER_NAME: {
      title: <Tooltip title="Patient Name" placement="bottom">Patient Name</Tooltip>,
      className: 'text-left text-elipsis',
      dataIndex: 'patientName',
      key: 'patientName',
      render: (patientName: any): any => ({
        children: getDefaultStr(patientName, true),
      }
      ),
      sorter: true,
      sortOrder: false,
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="patientName" />,
      ...getColumnSearchDropdown({
        filterKey: 'patientName',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.TEXT,
        placeholder: 'Search Member Name',
      }),
    },
    CLAIMED_AMOUNT: {
      title: 'Claimed Amount',
      key: 'claimedAmount',
      dataIndex: 'claimedAmount',
      className: 'column-money text-left',
      render: (amount: any): any => (
        <NumberFormatView prefix="₹ " amount={amount} fixedDecimalScale={true} />
      ),
      sorter: true,
      sortOrder: false,
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="claimedAmount" />,
      ...getColumnSearchDropdown({
        filterKey: 'claimedAmount',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.AMOUNT,
        placeholder: 'Search Claimed Amount',
      }),
    },
    APPROVED_AMOUNT: {
      title: 'Approved Amount',
      key: 'approvedAmount',
      dataIndex: 'approvedAmount',
      className: 'column-money text-right',
      render: (amount: any): any => (
        <NumberFormatView prefix="₹ " amount={amount} fixedDecimalScale={true} />
      ),
      sorter: true,
      sortOrder: false,
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="approvedAmount" />,
      ...getColumnSearchDropdown({
        filterKey: 'approvedAmount',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.AMOUNT,
        placeholder: 'Search Approved Amount',
      }),
    },
    STATUS: {
      title: <Tooltip title="Claim status" placement="bottom">Status</Tooltip>,
      dataIndex: 'statusName',
      key: 'statusName',
      render: (status: any, data: any): any => {
        let statusClassName = '';

        CRM_INPATIENT_STATUS_COLOUR.map((colorObject: any): any => {
          if (colorObject.StatusId === data?.statusId) {
            statusClassName = colorObject.class;
          }
        });
        return (
          <span className={`fsize_sm bold status-text ${statusClassName}`}>{status && status}</span>
        );
      },
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="statusId" />,
    },
    DOA: {
      title: (
        <Tooltip className="force-single-line" title="Date of Admission" placement="bottom">
          DOA <Information className="info-icon" />
        </Tooltip>),
      dataIndex: 'doa',
      key: 'doa',
      render: (date: any): any => (
        date && getDateWithTextMonth(date)
      ),
      sorter: true,
      sortOrder: false,
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="doa" />,
      ...getColumnSearchDropdown({ filterKey: 'doa', inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.DATE_RANGE }),
    },
    DOD: {
      title: (
        <Tooltip className="force-single-line" title="Date of Discharge" placement="bottom">
          DOD <Information className="info-icon" />
        </Tooltip>),
      dataIndex: 'dod',
      key: 'dod',
      render: (date: any): any => (
        date && getDateWithTextMonth(date)
      ),
      sorter: true,
      sortOrder: false,
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="dod" />,
      ...getColumnSearchDropdown({ filterKey: 'dod', inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.DATE_RANGE }),
    },
  };
  return isNewAdmissionBOH ? {
    CENTER: {
      title: 'Center',
      dataIndex: 'providerName',
      key: 'providerName',
      className: 'text-elipsis',
      render: (providerName: any, row: any): any => (
        getTruncatedString(getDefaultStr(providerName, true))
      ),
      sorter: true,
      sortOrder: false,
      filterIcon: (): any => <CustomFilterDropdownIcon filterKey="providerId" />,
      ...getColumnSearchDropdown({
        filterKey: 'providerId',
        inputType: IP_DROPDOWN_SEARCH_INPUT_TYPES.MULTISELECT_DROPDOWN,
        placeholder: 'Search Center',
        optionPath: '[\'applicationDetail\'][\'_applicationDetail\'][\'groupProviders\']',
        labelKey: 'providerName',
        additionalLabelKey: 'providerRohiniCode',
        additionalLabelKeyDivider: '|',
      }),
    },
    ...grievanceTableMappingColumns,
  } : grievanceTableMappingColumns;
};

export const GRIEVANCE_DATA = ['Delay in Payer Response', 'Clarification on Approved Amount', 'Reconsideration of Denial', 'Post settlement grievance', 'Others'];
export const prepareEmail = (claims: any[] = [], payer: Payer, grievanceReason: any): any => {
  const appDetail = ApplicationDetail.instance;
  const claimText = `claim${claims?.length > 1 ? 's' : ''}`;
  const subject = `Grievance Raised on ${claims?.length} ${claimText} by ${appDetail.getProviderName()}`;
  const { name } = payer;

  const claimData = claims?.map((claim: any, index: number): string => {
    return `<p>
      <div><b>Sl. no.:</b>&nbsp;<span>${index + 1}</span></div>
      <div><b>Claim no.:</b>&nbsp;<span>${claim?.payerClaimNo || '-'}</span></div>
      <div><b>Patient name:</b>&nbsp;<span>${claim?.patientName}</span></div>
      <div><b>Claimed amount:</b>&nbsp;<span>${claim?.claimedAmount || '-'}</span></div>
      <div><b>Approved amount:</b>&nbsp;<span>${claim?.approvedAmount || '-'}</span></div>
      <div><b>Grievance type:</b>&nbsp;<span>${grievanceReason}</span></div>
    </p>`;
  });

  const body = `
    <div>
      <div>Dear ${name}</div>
      <div>
        ${appDetail.getProviderName()} has raised a grievance for ${claims?.length} ${claimText}. The details of the Claims are below.
      </div>
      <div>
        ${claimData}
      </div>
      <div>
        Regards,
      </div>
      <div>
        ${appDetail.getProviderName()}
      </div>
    </div>
  `;
  return { subject, body };
};

export const GRIEVANCE_TYPE_PLACEHOLDER = 'Select the reason for Grievance';

export const GRIEVANCE_DATE_FORMAT = 'dd MMM yyyy, hh:mm a';
